"use client";

import React from "react";

import cx from "classnames";
import { useParams, usePathname } from "next/navigation";
import useSizeUnit from "@/hooks/useSizeUnit";
import { useClickAway } from "react-use";
import { useTranslations } from "next-intl";

import Icon from "@ide/shared/components/Icon";

const SizeUnitSelector = ({
  id = "top",
  dropDownDirection = "down",
  dropDownFromSide = "right",
  theme = "light",
}) => {
  const t = useTranslations();
  const { currentSizeUnit, updateCurrentSizeUnit } = useSizeUnit();
  const [showSizeUnitsDropdown, setShowSizeUnitsDropdown] = React.useState(false);

  const ref = React.useRef(null);
  useClickAway(ref, () => {
    setShowSizeUnitsDropdown(false);
  });

  const units = React.useMemo(
    () => [
      { value: "sqm", label: t("common.size_unit.square_meters") },
      { value: "sqft", label: t("common.size_unit.square_feet") },
    ],
    [t],
  );

  const switchSizeUnit = React.useCallback(
    (newSizeUnit: string) => {
      updateCurrentSizeUnit(newSizeUnit);
    },
    [updateCurrentSizeUnit],
  );

  return (
    <div
      className={cx("currency-selector inline-block relative", {
        "text-white": theme === "dark",
      })}
      ref={ref}
    >
      <button
        className={cx(
          "flex items-center flex-nowrap gap-1.5 px-1 md:px-2 py-1 font-medium rounded-md",
          {
            "hover:bg-gray-100": theme === "light",
            "hover:bg-neutral-800": theme === "dark",
          },
        )}
        onClick={() => setShowSizeUnitsDropdown((prev) => !prev)}
      >
        <span>{t(`common.size_unit.${currentSizeUnit}`)}</span>

        <Icon icon="fa-solid fa-caret-down" className="w-2" />
      </button>

      {showSizeUnitsDropdown && (
        <div
          className={cx("absolute", {
            "top-full": dropDownDirection === "down",
            "bottom-full": dropDownDirection === "up",
            "right-0": dropDownFromSide === "right",
            "left-0": dropDownFromSide === "left",
          })}
        >
          <div
            className={cx("px-3 py-3 rounded-sm shadow-md", {
              "bg-white": theme === "light",
              "bg-black": theme === "dark",
            })}
          >
            <ul className="text-sm">
              {units
                .filter(({ label, value }) => value !== currentSizeUnit)
                .map(({ label, value }) => (
                  <li key={`${id}-${value}`}>
                    <button
                      onClick={() => {
                        switchSizeUnit(value);
                        setShowSizeUnitsDropdown(false);
                      }}
                      className={cx("block px-2 py-1 text-medium rounded-md whitespace-nowrap", {
                        "hover:bg-gray-100": theme === "light",
                        "hover:bg-neutral-800": theme === "dark",
                      })}
                    >
                      {label}
                    </button>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default SizeUnitSelector;
